import type { ComponentContentSchema } from '@vibesites/runtime';
import { RichTextContent } from '@vibesites/runtime/react';

/**
 * WhiteBlock — a plain white spacer band. Intended to sit above any hero
 * section as the white backdrop that the fixed Header floats over (the
 * Header is `position: fixed; z-50` with no spacer of its own). The
 * min-height must be tall enough to fully cover the Header so the next
 * section doesn't bleed up behind the nav.
 *
 * Header dimensions (from Header.tsx):
 *   - Mobile: main nav row only, `h-24` (96px)
 *   - lg+:    utility row (~50px) + main nav row `h-32` (128px) ≈ 180px
 *
 * Plus the optional SpecialOfferBanner, whose height the Header tracks via
 * `--offer-banner-height` and shifts down by — so this block mirrors that
 * offset so both stretch in lock-step when the banner is shown.
 *
 * Carries one optional rich-text token (centered) so editors can drop a
 * short headline into the band; otherwise it renders as pure white space.
 */
export function WhiteBlock() {
  return (
    <section
      className="oasis-white-block w-full bg-white flex items-center justify-center"
    >
      <RichTextContent
        path="whiteblock.content"
        label="Content"
        defaultValue=""
        as="div"
        className="font-body text-base text-[var(--vibe-color-text)] text-center px-6"
      />
      <style>{`
        .oasis-white-block {
          /* Mobile: just clear the 96px main nav row. */
          min-height: calc(var(--offer-banner-height, 0px) + 100px);
          transition: min-height 0.5s cubic-bezier(0.4, 0, 0.2, 1);
        }
        @media (min-width: 1024px) {
          .oasis-white-block {
            /* lg+: just clear the 50px utility row + 128px main nav (≈180px). */
            min-height: calc(var(--offer-banner-height, 0px) + 185px);
          }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'WhiteBlock',
  displayName: 'White Block',
  tokens: {
    'whiteblock.content': {
      label: 'Content',
      type: 'richtext',
      defaultValue: '',
    },
  },
  slotGroups: {},
};

(WhiteBlock as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default WhiteBlock;
